import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";
import "../Styles/PageParts.css";

/**
 * Shared furniture for the inner pages.
 *
 * Every page used to ship its own hero and its own closing call to action,
 * which is the main reason the site read as a dozen different websites. These
 * two components replace all of them.
 */

/**
 * @param {string}  eyebrow  Small uppercase label above the title.
 * @param {node}    title    Heading content; wrap one phrase in <em> for the
 *                           editorial italic.
 * @param {string}  lede     One paragraph, kept short on purpose.
 * @param {array}   stats    [{ figure, label }] — omit rather than invent.
 * @param {array}   ctas     [{ to, href, label, variant }]
 * @param {string}  image    Background photograph.
 */
export function PageHero({ eyebrow, title, lede, stats = [], ctas = [], image }) {
  return (
    <section className="vp-hero">
      {image && (
        <div className="vp-hero__media" aria-hidden="true">
          <img src={image} alt="" fetchpriority="high" decoding="async" />
          <div className="vp-hero__scrim" />
        </div>
      )}

      <div className="ve-container vp-hero__inner">
        {eyebrow && <span className="ve-eyebrow vp-hero__eyebrow">{eyebrow}</span>}

        <h1 className="vp-hero__title">{title}</h1>

        {lede && <p className="ve-lede vp-hero__lede">{lede}</p>}

        {ctas.length > 0 && (
          <div className="vp-hero__ctas">
            {ctas.map((c) =>
              c.to ? (
                <Link
                  key={c.label}
                  to={c.to}
                  className={`ve-btn ${c.variant === "ghost" ? "ve-btn--ghost" : "ve-btn--mint"}`}
                >
                  {c.label} {c.variant !== "ghost" && <FaArrowRight />}
                </Link>
              ) : (
                <a
                  key={c.label}
                  href={c.href}
                  className={`ve-btn ${c.variant === "ghost" ? "ve-btn--ghost" : "ve-btn--mint"}`}
                >
                  {c.label} {c.variant !== "ghost" && <FaArrowRight />}
                </a>
              )
            )}
          </div>
        )}

        {stats.length > 0 && (
          <dl className="vp-hero__stats">
            {stats.map((s) => (
              <div key={s.label}>
                <dt>{s.figure}</dt>
                <dd>{s.label}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </section>
  );
}

/** The closing call to action, identical on every page. */
export function CtaBand({
  title = "Not sure which pathway fits you?",
  body = "Book a free one-on-one sitting with a senior counsellor in Andheri West, or on video if that is easier. You will leave knowing exactly where you stand.",
  primary = { to: "/consultationForm", label: "Book a free consultation" },
  secondary = { to: "/faq", label: "Read common questions" },
}) {
  return (
    <section className="ve-section ve-section--tight vp-cta">
      <div className="ve-container vp-cta__inner">
        <div className="vp-cta__copy">
          <h2 className="ve-h2">{title}</h2>
          <p className="ve-lede">{body}</p>
        </div>

        <div className="vp-cta__actions">
          <Link to={primary.to} className="ve-btn ve-btn--mint">
            {primary.label} <FaArrowRight />
          </Link>
          {secondary && (
            <Link to={secondary.to} className="ve-btn ve-btn--ghost">
              {secondary.label}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
